/**
 * Background warming for every area a user has available.
 *
 * The complaint this answers: opening an area meant waiting for it to be
 * "recomputed" every session. It never needed recomputing — the cost was
 * shipping clipped polygons (10.5 MB for a large area vs 81 KB without them).
 * So warming fetches the geometry-free form for every area the user can open,
 * in the background, and caches it. By the time they click one it is already
 * there; geometry is fetched only for the area actually opened.
 *
 * Scope note: the areas warmed here are whatever this client knows about — the
 * domain's customer areas plus the locally saved AOI library. Warming areas
 * "in a user's account" across devices additionally needs the AOI library to
 * live server-side keyed by the authenticated user, which it does not yet.
 */

import { ref, readonly } from 'vue';
import { useAoiStore } from './useAoiStore';

/** Concurrency: the predictor is fast, but each warm is still a large POST. */
const MAX_PARALLEL = 2;

/** Give the initial map render the network to itself before warming starts. */
const START_DELAY_MS = 1500;

export interface WarmableArea {
  name: string;
  geojson: Record<string, unknown>;
}

const warming = ref(false);
const warmedCount = ref(0);
const totalCount = ref(0);
const warmedNames = ref<string[]>([]);

let inFlight: Promise<void> | null = null;

async function warmOne(area: WarmableArea): Promise<void> {
  const { submitCoverage } = useAoiStore();
  try {
    // quiet: never touches the visible status/result or raises a banner.
    // includeGeometry false: this is the cheap form that makes warming viable.
    await submitCoverage({ geojson: area.geojson, includeGeometry: false, quiet: true });
    warmedNames.value = [...warmedNames.value, area.name];
  } catch {
    // A failed warm is a non-event: the area still opens, it just isn't instant.
  } finally {
    warmedCount.value += 1;
  }
}

/**
 * Warm every supplied area, at most MAX_PARALLEL at a time.
 *
 * Idempotent per session: a second call while the first is running joins it
 * rather than doubling the request load.
 */
export function warmAreas(areas: WarmableArea[]): Promise<void> {
  if (inFlight) return inFlight;
  if (!areas.length) return Promise.resolve();

  warming.value = true;
  warmedCount.value = 0;
  warmedNames.value = [];
  totalCount.value = areas.length;

  const queue = [...areas];
  const runWorker = async (): Promise<void> => {
    for (;;) {
      const next = queue.shift();
      if (!next) return;
      await warmOne(next);
    }
  };

  inFlight = (async () => {
    await new Promise((resolve) => setTimeout(resolve, START_DELAY_MS));
    await Promise.all(
      Array.from({ length: Math.min(MAX_PARALLEL, areas.length) }, () => runWorker()),
    );
    warming.value = false;
    inFlight = null;
  })();

  return inFlight;
}

export function useAoiWarming() {
  return {
    warming: readonly(warming),
    warmedCount: readonly(warmedCount),
    totalCount: readonly(totalCount),
    warmedNames: readonly(warmedNames),
    warmAreas,
  };
}
